'use client';

import { type RefObject, useEffect, useRef } from 'react';

import { type PluginHostMessage } from '~/utils/pluginHostMessaging';

export type PluginFrameListenerConfig<TMessage extends PluginHostMessage> = {
  /** The embedded picker/child iframe. Messages are only accepted from its `contentWindow`. */
  frameRef: RefObject<HTMLIFrameElement>;
  /** The origin the embedded frame is served from — only messages whose `event.origin` matches are accepted. */
  frameOrigin: string;
  /** Type guard validating the message shape posted up by the frame. */
  isFrameMessage: (data: unknown) => data is TMessage;
  /** Callback for each validated message — source, origin, and shape are already checked. */
  onMessage: (message: TMessage) => void;
};

/**
 * Host-side counterpart to {@link usePluginHostListener}: for the parent document that embeds a
 * picker iframe and listens for messages the frame posts *up*. Every message passes two independent
 * checks before dispatch:
 * 1. `event.source === frameRef.current.contentWindow` — from the frame we embedded.
 * 2. `event.origin === frameOrigin` — from that frame's origin, nothing else.
 *
 * `isFrameMessage` then narrows the untrusted `event.data`. The listener attaches once and reads the
 * latest config via a ref, so changing callbacks between renders never drops an in-flight message.
 */
export function usePluginFrameListener<TMessage extends PluginHostMessage>(
  config: PluginFrameListenerConfig<TMessage>,
): void {
  const configRef = useRef(config);

  useEffect(() => {
    configRef.current = config;
  });

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { frameRef, frameOrigin, isFrameMessage, onMessage } = configRef.current;
      const frameWindow = frameRef.current?.contentWindow;

      // Source check: the message must come from the frame we embedded.
      if (!frameWindow || event.source !== frameWindow) {
        return;
      }

      // Origin check: only the origin the frame is served from.
      if (event.origin !== frameOrigin) {
        return;
      }

      if (!isFrameMessage(event.data)) {
        return;
      }

      onMessage(event.data);
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);
}
